import { ReactNode, useState } from 'react';
import {
  Box,
  Container,
  Divider,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';

import { Button } from 'components/Button';
import { EnvelopeIcon } from 'components/Icons';
import { ResultModal } from 'components/ResultModal';

/**
 * Page de debug (non liée au métier) : aperçu des modales de confirmation
 * affichées après un transfert de dossier, en mode "simply" et en mode classique.
 *
 * Accessible via la route publique /debug.
 * Le contenu "simply" reproduit `transfertSuccessContent` de TagActionList.
 */

// Encart "simply" affiché sous le message après un transfert réussi.
// etoiles <= 2 => dossier non finançable.
const simplySuccessContent = (etoiles?: number): ReactNode => {
  const nonFinancable = etoiles !== undefined && etoiles <= 2;
  return (
    <VStack spacing={4}>
      {etoiles !== undefined && etoiles <= 2 && (
        <Box bg="red.50" borderRadius="lg" px={5} py={3}>
          <Text color="red.500" fontWeight="semibold" fontSize="sm">
            Ce dossier est non finançable (scoré {etoiles} étoile{etoiles > 1 ? 's' : ''}), il
            nécessite une analyse plus approfondie par notre équipe.
            <br />
            Un expert courtier contactera votre client dès sa prise en charge.
          </Text>
        </Box>
      )}
      {!nonFinancable && (
        <Text color="gray.600">
          Votre acquéreur sera contacté sous 48h00 maximum par un courtier expert.
        </Text>
      )}
      <Text color="gray.600">
        Nous vous invitons à passer au pack supérieur afin d&apos;être éligible à une commission.
      </Text>
      <Box>
        <Text color="gray.600">Contactez-nous par mail :</Text>
        <Stack direction="row" align="center" justify="center" spacing={2}>
          <EnvelopeIcon boxSize="18px" color="teal.500" />
          <Link href="mailto:direction@monemprunt.com" color="teal.500" fontWeight="semibold">
            direction@monemprunt.com
          </Link>
        </Stack>
      </Box>
    </VStack>
  );
};

// Message principal affiché en mode simply après un transfert réussi.
const SIMPLY_MESSAGE = 'Le dossier a été transféré à un courtier Monemprunt.com pour traitement.';

type Example = {
  label: string;
  description: string;
  success: boolean;
  message: string;
  extraContent?: ReactNode;
};

const EXAMPLES: Example[] = [
  {
    label: 'Transfert réussi — mode classique (non simply)',
    description: 'Message API + délai de contact (48h00).',
    success: true,
    message: 'Dossier transféré avec succès à un courtier Monemprunt.com.',
    extraContent: (
      <Text color="gray.600">
        Votre acquéreur sera contacté sous 48h00 maximum par un courtier expert.
      </Text>
    ),
  },
  {
    label: 'Transfert réussi — mode simply (finançable)',
    description: 'Dossier finançable (scoring > 2 étoiles) : contact sous 48h00.',
    success: true,
    message: SIMPLY_MESSAGE,
    extraContent: simplySuccessContent(4),
  },
  {
    label: 'Transfert réussi — mode simply (non finançable)',
    description: 'Dossier non finançable (scoring ≤ 2 étoiles) : alerte + contact sous 5 jours.',
    success: true,
    message: SIMPLY_MESSAGE,
    extraContent: simplySuccessContent(2),
  },
  {
    label: 'Transfert en échec',
    description: "Message d'erreur renvoyé par l'API (icône rouge).",
    success: false,
    message: 'Le transfert a échoué : le montant du projet est invalide.',
  },
];

export const DebugPageRoute = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [current, setCurrent] = useState<Example | null>(null);

  const openExample = (example: Example) => {
    setCurrent(example);
    onOpen();
  };

  return (
    <Container maxW="container.md" py={10}>
      <Heading size="lg" mb={2}>
        Debug — modales après transfert
      </Heading>
      <Text color="gray.600" mb={6}>
        Aperçu des modales de confirmation (ResultModal) affichées après le transfert d&apos;un
        dossier, selon le mode de l&apos;organisation.
      </Text>
      <Divider mb={6} />

      <VStack spacing={4} align="stretch">
        {EXAMPLES.map((example) => (
          <Box key={example.label} borderWidth={1} borderColor="gray.200" borderRadius="lg" p={4}>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              align={{ md: 'center' }}
              justify="space-between"
              spacing={3}
            >
              <Box>
                <Text fontWeight="semibold">{example.label}</Text>
                <Text fontSize="sm" color="gray.500">
                  {example.description}
                </Text>
              </Box>
              <Button
                colorScheme={example.success ? 'teal' : 'gray'}
                onClick={() => openExample(example)}
                flexShrink={0}
              >
                Afficher
              </Button>
            </Stack>
          </Box>
        ))}
      </VStack>

      <ResultModal
        isOpen={isOpen}
        onClose={onClose}
        success={current?.success ?? false}
        message={current?.message ?? ''}
        extraContent={current?.extraContent}
      />
    </Container>
  );
};
